import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UserType } from "@/constants";
import { getRegistrantAccountOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import {
  answerOptionsToSelectOptions,
  getFirstAnswerOptionKey,
} from "@/lib/answer-option-order";
import { nacFieldRequirementsForValidation } from "@/lib/contact-question-requirements";
import { accountDataForNacRegistrantCopy } from "@/lib/license-nac-account-data";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import {
  QuestionField,
  companyCopyFieldProps,
  extractBracketLabel,
  validatorForQuestionType,
} from "../../registrant/form-utils/question-field-utils";

/** Nothing was answered yet; contact answers can be stored as an object with only empty fields. */
function isBlankAnswer(answer: unknown): boolean {
  if (answer === null || answer === undefined || answer === "") return true;
  if (typeof answer === "string") {
    try {
      return isBlankAnswer(JSON.parse(answer));
    } catch {
      return false;
    }
  }
  if (Array.isArray(answer)) return answer.length === 0;
  if (typeof answer === "object") {
    return Object.values(answer as Record<string, unknown>).every(
      (value) =>
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === ""),
    );
  }
  return false;
}

interface EditSingleAdditionalInfoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  data: LicenseResponseSchema;
  question: QuestionResponseSchema | null;
  /** Other questions on this registration, used for configured company copy sources. */
  questions?: QuestionResponseSchema[] | null;
  onSave?: (answers: Record<string, any>) => Promise<void>;
  userType: UserType;
  elevated?: boolean;
}

export function EditSingleAdditionalInfoDialog({
  isOpen,
  onOpenChange,
  data,
  question,
  questions,
  onSave,
  userType,
  elevated = false,
}: EditSingleAdditionalInfoDialogProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const { data: registrantApiAccount } = useQuery({
    ...getRegistrantAccountOptions(),
    enabled: isAuthSetupComplete && userType === UserType.REGISTRANT,
  });

  const nacCopyAccountData = useMemo(
    () =>
      accountDataForNacRegistrantCopy(
        userType,
        data.registrant_account,
        registrantApiAccount,
      ),
    [userType, data.registrant_account, registrantApiAccount],
  );

  const [answer, setAnswer] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [forceValidation, setForceValidation] = useState(false);

  const validationSchema = useMemo(() => {
    if (!question) return z.any();
    return validatorForQuestionType(
      question.answer_type,
      question.answer_type === "boolean" ||
        question.answer_type === "single_select" ||
        ((question.answer_required as boolean) ?? false),
      isNacStyleAnswerType(question.answer_type)
        ? ((question as { select_from_fixed_list?: boolean })
            .select_from_fixed_list ?? false)
        : undefined,
      isNacStyleAnswerType(question.answer_type)
        ? nacFieldRequirementsForValidation(question)
        : undefined,
      question.allow_multiple_urls ?? false,
      question.allow_multiple_documents ?? false,
    );
  }, [question]);

  useEffect(() => {
    if (isOpen && question) {
      let answerValue =
        data.answers_to_additional_questions?.[question.id] ?? null;
      if (
        question.answer_type === "single_select" &&
        question.answer_options &&
        Object.keys(question.answer_options).length > 0 &&
        (answerValue === null ||
          answerValue === undefined ||
          answerValue === "")
      ) {
        answerValue =
          getFirstAnswerOptionKey(
            question.answer_options,
            question.answer_option_keys_order,
          ) ?? null;
      }
      setAnswer(answerValue);
      setError(null);
      setForceValidation(false);
    }
  }, [isOpen, question, data.answers_to_additional_questions]);

  const validate = () => {
    if (!question) return true;
    const result = validationSchema.safeParse(answer);
    if (!result.success) {
      // Show first error message if available
      const firstError = (result as any).error?.errors?.[0]?.message as
        | string
        | undefined;
      setError(firstError ?? "Invalid value");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!question) return;
    setForceValidation(true);
    const isValid = validate();
    if (!isValid) return;
    if (onSave) {
      await onSave({ [question.id]: answer });
      onOpenChange(false);
    }
  };

  if (!question) return null;

  const existingAnswer = data.answers_to_additional_questions?.[question.id];
  const isNewAnswer = isBlankAnswer(existingAnswer);

  const field = {
    state: {
      value: answer,
      errors: error ? [error] : undefined,
    },
    handleChange: (value: any) => setAnswer(value),
    handleBlur: () => {
      if (forceValidation) validate();
    },
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        elevated={elevated}
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {isNewAnswer ? "Add" : "Edit"} {question.question}
          </DialogTitle>
          <DialogDescription>
            {isNewAnswer
              ? "Enter the information for this field."
              : "Update the information for this field."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <QuestionField
            answerType={question.answer_type}
            allowMultipleUrls={question.allow_multiple_urls ?? false}
            allowMultipleDocuments={question.allow_multiple_documents ?? false}
            field={field}
            questionId={question.id}
            selectFromFixedList={
              isNacStyleAnswerType(question.answer_type)
                ? ((question as { select_from_fixed_list?: boolean })
                    .select_from_fixed_list ?? false)
                : undefined
            }
            nacRequirements={
              isNacStyleAnswerType(question.answer_type)
                ? nacFieldRequirementsForValidation(question)
                : undefined
            }
            useContactLabel={
              question.answer_type === "company_contact" &&
              Boolean(
                (question as { use_contact_label?: boolean }).use_contact_label,
              )
            }
            answerOptions={
              question.answer_type === "single_select" ||
              question.answer_type === "multi_select"
                ? question.answer_options
                  ? answerOptionsToSelectOptions(
                      question.answer_options,
                      question.answer_option_keys_order,
                    )
                  : undefined
                : undefined
            }
            questionName={question.question}
            accountData={nacCopyAccountData}
            currentQuestionBracketLabel={
              isNacStyleAnswerType(question.answer_type)
                ? (extractBracketLabel(question.question) ?? undefined)
                : undefined
            }
            forceValidation={forceValidation}
            userType={userType}
            allowCopyRegistrantInfo={
              isNacStyleAnswerType(question.answer_type)
                ? ((question as { allow_copy_registrant_info?: boolean })
                    .allow_copy_registrant_info ?? true)
                : undefined
            }
            {...companyCopyFieldProps({
              question,
              questions: questions ?? (question ? [question] : []),
              answers: {
                ...(data.answers_to_additional_questions as
                  | Record<string, unknown>
                  | null
                  | undefined),
                [question.id]: answer,
              },
              registrantAccountId: data.registrant_account?.id,
              excludeLicenseId: data.id,
            })}
          />

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={!!error}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
